/**
 * Task Date Utilities
 * 
 * Helper functions for categorizing and formatting task dates
 */
import {
  buildDateKey,
  coerceDateValue,
  resolveLocale,
  resolveTimeZone,
} from '../../../../../utils/dateUtils';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const translate = (t, key, options = {}, fallback = key) => {
  if (typeof t === 'function') {
    return t(key, { ...options, defaultValue: fallback });
  }
  return fallback;
};

const parseDateKey = (dateKey) => {
  if (typeof dateKey !== 'string') return null;

  const match = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  const utcMillis = Date.UTC(year, month - 1, day);
  const parsed = new Date(utcMillis);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return Math.floor(utcMillis / DAY_IN_MS);
};

const getDayDifference = (leftKey, rightKey) => {
  const leftDay = parseDateKey(leftKey);
  const rightDay = parseDateKey(rightKey);
  if (leftDay === null || rightDay === null) return null;
  return leftDay - rightDay;
};

const getHourMinuteInTimeZone = (date, timeZone) => {
  const hourMinuteParts = new Intl.DateTimeFormat('en-US-u-nu-latn-hc-h23', {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);

  const hourPart = hourMinuteParts.find((part) => part.type === 'hour')?.value;
  const minutePart = hourMinuteParts.find((part) => part.type === 'minute')?.value;
  if (!hourPart || !minutePart) {
    return null;
  }

  const hours = Number.parseInt(hourPart, 10);
  const minutes = Number.parseInt(minutePart, 10);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null;
  }

  return {
    hours: hours % 24,
    minutes,
  };
};

const hasSpecificTime = (date, timeZone) => {
  const parts = getHourMinuteInTimeZone(date, timeZone);
  if (!parts) {
    return false;
  }

  return parts.hours !== 0 || parts.minutes !== 0;
};

/**
 * Get the status of a due date (overdue, today, upcoming)
 * @param {Date|Firebase.Timestamp|string} dueDate - The due date
 * @param {Object} options - Optional status options
 * @param {Date|Firebase.Timestamp|string} options.now - Reference date for deterministic behavior
 * @param {string} options.timeZone - Optional timezone used for day boundary calculations
 * @returns {string} - 'overdue', 'today', or 'upcoming'
 */
export const getDueDateStatus = (dueDate, { now = new Date(), timeZone } = {}) => {
  const date = coerceDateValue(dueDate);
  if (!date) {
    return 'upcoming';
  }

  const nowDate = coerceDateValue(now) || new Date();
  const safeTimeZone = resolveTimeZone(timeZone);
  const dueDateKey = buildDateKey(date, { timeZone: safeTimeZone });
  const todayKey = buildDateKey(nowDate, { timeZone: safeTimeZone });

  if (!dueDateKey || !todayKey) {
    return 'upcoming';
  }

  if (dueDateKey < todayKey) {
    return 'overdue';
  }

  if (dueDateKey === todayKey) {
    return 'today';
  }

  return 'upcoming';
};

/**
 * Format a due date for display
 * @param {Date|Firebase.Timestamp|string} dueDate - The due date
 * @param {boolean} isComplete - Whether the task is complete
 * @param {Object} options - Formatting options
 * @param {string} options.locale - Locale for Intl formatting
 * @param {Date|Firebase.Timestamp|string} options.now - Reference date for deterministic behavior
 * @param {string} options.timeZone - Optional timezone for formatting and day boundaries
 * @param {Function} options.t - Translation function
 * @returns {string} - Formatted date string
 */
export const formatDueDate = (
  dueDate,
  isComplete,
  { locale, now = new Date(), timeZone, t } = {}
) => {
  const date = coerceDateValue(dueDate);
  if (!date) {
    return '';
  }

  const localeCode = resolveLocale(locale);
  const safeTimeZone = resolveTimeZone(timeZone);
  const nowDate = coerceDateValue(now) || new Date();
  const todayKey = buildDateKey(nowDate, { locale: localeCode, timeZone: safeTimeZone });
  const dueDateKey = buildDateKey(date, { locale: localeCode, timeZone: safeTimeZone });

  if (!todayKey || !dueDateKey) {
    return '';
  }

  const tomorrow = new Date(nowDate);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(nowDate);
  yesterday.setDate(yesterday.getDate() - 1);
  const tomorrowKey = buildDateKey(tomorrow, { locale: localeCode, timeZone: safeTimeZone });
  const yesterdayKey = buildDateKey(yesterday, { locale: localeCode, timeZone: safeTimeZone });

  const timeLabel = new Intl.DateTimeFormat(localeCode, {
    hour: 'numeric',
    minute: '2-digit',
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  }).format(date);

  const weekdayLabel = new Intl.DateTimeFormat(localeCode, {
    weekday: 'long',
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  }).format(date);

  const currentYear = new Intl.DateTimeFormat(localeCode, {
    year: 'numeric',
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  }).format(nowDate);

  const dueYear = new Intl.DateTimeFormat(localeCode, {
    year: 'numeric',
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  }).format(date);

  const fullDateLabel = new Intl.DateTimeFormat(localeCode, {
    month: 'short',
    day: 'numeric',
    ...(dueYear !== currentYear ? { year: 'numeric' } : {}),
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  }).format(date);

  if (isComplete) {
    return translate(
      t,
      'records.time.task.completed_today',
      {},
      'Completed today'
    );
  }

  if (dueDateKey === todayKey) {
    if (hasSpecificTime(date, safeTimeZone)) {
      return translate(
        t,
        'records.time.task.today_at',
        { time: timeLabel },
        `Today at ${timeLabel}`
      );
    }
    return translate(t, 'records.time.today', {}, 'Today');
  }

  if (dueDateKey === yesterdayKey) {
    return translate(t, 'records.time.yesterday', {}, 'Yesterday');
  }

  if (dueDateKey === tomorrowKey) {
    return translate(t, 'records.time.tomorrow', {}, 'Tomorrow');
  }

  if (dueDateKey < todayKey) {
    const daysAgo = getDayDifference(todayKey, dueDateKey);
    if (daysAgo === 1) {
      return translate(t, 'records.time.yesterday', {}, 'Yesterday');
    }
    if (typeof daysAgo === 'number' && daysAgo > 1) {
      return translate(
        t,
        'records.time.relative.day_ago',
        { count: daysAgo },
        `${daysAgo} days ago`
      );
    }
    return translate(t, 'records.time.yesterday', {}, 'Yesterday');
  }

  const daysUntil = getDayDifference(dueDateKey, todayKey);
  if (typeof daysUntil === 'number' && daysUntil < 7) {
    return weekdayLabel;
  }

  return fullDateLabel;
};
